import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.management_api.caja_session_records;

// Helper function to configure the Axios instance
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'management_api',
  group: 'caja_session_records',
  defaultTtl: '30s',
  hashLargeSegments: true,
  emitMeta: true,
});

// /GetCajaSessionRecordsByLocalIdAndDateRange/<int:local_id>
export const getCajaSessionRecordsByLocalIdAndDateRange = async (localId: number, startDate: string, endDate: string) => {
  return await retryWithBackoff(() =>
    api.post(`/GetCajaSessionRecordsByLocalIdAndDateRange/${localId}`, {
      start_date: startDate,
      end_date: endDate
    })
  );
};


// /CreateCajaSessionRecord
export const createCajaSessionRecord = async (data: any) => {
  const res = await retryWithBackoff(() =>
    api.post(`/CreateCajaSessionRecord`, data)
  );
  await cachedApi.clear();
  return res;
};

// /CancelCajaRecordByRecordId/<int:caja_session_record_id>

export const cancelCajaRecordByRecordId = async (recordId: number) => {
  const res = await retryWithBackoff(() =>
    api.post(`/CancelCajaRecordByRecordId/${recordId}`)
  );
  await cachedApi.clear();
  return res;
};

/**
 * \@ns.route('/IsSessionOpenForOrders/<int:local_id>')
@ns.param('local_id', 'The local identifier')
class IsSessionOpenForOrders(Resource):
    @ns.doc('Check if the current session and shift are open for orders')
    @jwt_required()
    def get(self, local_id):
        """
        Check if orders can be created for the current session/shift.
        Returns state: true if orders can be created (no caja record for current shift).
        Returns state: false if a caja record exists for current shift (session closed for accounting).
        """
        try:
            # Get the active session for the local
            active_session = LocalSession.query.filter_by(
                local_id=local_id,
                active=True
            ).first()
            
            if not active_session:
                return {
                    "state": False,
                    "message": "No hay sesión activa para este local"
                }, 200
            
            # Get the active shift_id (if using shifts)
            active_shift_id = active_session.active_shift_id if active_session.active_shift_id else None
            
            # Check if a caja session record exists for the current session and shift
            if active_shift_id:
                # Shift mode: check for record with current session AND current shift
                existing_record = CajaSessionRecord.query.filter_by(
                    session_id=active_session.local_session_id,
                    shift_id=active_shift_id,
                    canceled=False
                ).first()
                
                if existing_record:
                    # Caja record exists for current shift - cannot take new orders
                    return {
                        "state": False,
                        "message": f"Ya existe un registro de caja para el turno actual",
                        "session_id": active_session.local_session_id,
                        "shift_id": active_shift_id
                    }, 200
                else:
                    # No caja record for current shift - can take orders
                    return {
                        "state": True,
                        "message": "Sesión abierta para pedidos",
                        "session_id": active_session.local_session_id,
                        "shift_id": active_shift_id
                    }, 200
            else:
                # No shift mode (backward compatibility): check for record with current session only
                existing_record = CajaSessionRecord.query.filter_by(
                    session_id=active_session.local_session_id,
                    canceled=False
                ).filter(
                    (CajaSessionRecord.shift_id.is_(None)) | (CajaSessionRecord.shift_id == 0)
                ).first()
                
                if existing_record:
                    # Caja record exists for session - cannot take new orders
                    return {
                        "state": False,
                        "message": "Ya existe un registro de caja para la sesión actual",
                        "session_id": active_session.local_session_id,
                        "shift_id": None
                    }, 200
                else:
                    # No caja record for session - can take orders
                    return {
                        "state": True,
                        "message": "Sesión abierta para pedidos",
                        "session_id": active_session.local_session_id,
                        "shift_id": None
                    }, 200

        except Exception as e:
            return {
                "state": False,
                "message": f"Error verificando estado de sesión: {str(e)}"
            }, 500

 */

export const isSessionOpenForOrders = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/IsSessionOpenForOrders/${localId}`)
  );
};
